import proptypes from 'prop-types';

const {
  arrayOf, bool, func, number, oneOf, oneOfType, shape, string, element, elementType, node, objectOf
} = proptypes;

export const imageWithIconDefaultProps = {
  className: '',
  height: null,
  width: null,
  lazy: true,
  children: null
};

export const imageWithIconPropTypes = {
  src: string.isRequired,
  alt: string.isRequired,
  className: string,
  height: number,
  width: number,
  lazy: bool,
  children: node
};

export const mainAssetDefaultProps = {
  mainAsset: {
    id: '',
    url: '',
    mediaType: ''
  },
  zoomedAsset: {
    id: '',
    url: '',
    mediaType: ''
  },
  fwdOverlayRef: null,
  itemId: '',
  showAvataarAR: false,
  showAvataarDefault: false,
  selectedThumbnailIndex: 0
};

export const mainAssetPropTypes = {
  mainAsset: shape({
    id: string,
    url: string,
    mediaType: string
  }),
  zoomedAsset: shape({
    id: string,
    url: string,
    mediaType: string
  }),
  fwdOverlayRef: shape({ current: elementType }),
  videoAnalyticsData: shape({
    section: string.isRequired,
    position: string.isRequired
  }).isRequired,
  itemId: string,
  showAvataarAR: bool,
  showAvataarDefault: bool,
  selectedThumbnailIndex: number
};

export const mainImageDefaultProps = {
  mainImage: null,
  mainImageFetchPriority: 'auto',
  overrideData: null,
  showAvataarDefault: false
};

export const mainImagePropTypes = {
  mainImage: shape({}),
  selectedThumbnailIndex: number.isRequired,
  media: shape({
    main: arrayOf(shape({})),
    highResolution: arrayOf(shape({})),
    zoom: arrayOf(shape({}))
  }).isRequired,
  identifiers: shape({
    brandName: string,
    productLabel: string
  }).isRequired,
  mainImageFetchPriority: string,
  overrideData: shape({
    brandNameLabel: string,
    galleryLabel: string
  }),
  showAvataarDefault: bool,
  onMainImageClick: func.isRequired
};

export const mediaGalleryHorizontalDefaultProps = {
  collectionId: null,
  mainImageFetchPriority: 'auto',
  hideProductImagesLabel: false,
  highlight: false,
  highRes: false,
  hover: false,
  itemId: null,
  configId: undefined,
  marqueeRef: null,
  overrideData: null,
  showLoader: false
};

export const mediaGalleryOverlayTabsDefaultProps = {
  collection: null,
  media: { main: [], thumbnails: [], zoom: [], highResolution: [] },
  product: { identifiers: { brandName: '', productLabel: '' }, itemId: '' },
  thumbnails: [],
  overrideData: null,
  configuredProductLabel: null,
  itemId: '',
  showAvataarAR: false,
  showAvataarDefault: false,
  initialType: 'IMAGE',
  initialId: null,
  selectedImageIndex: 0
};

export const mediaGalleryOverlayTabsPropTypes = {
  collection: shape({}),
  handleClose: func.isRequired,
  media: shape({
    main: arrayOf(shape({ id: string, type: string, url: string, videoStill: string })),
    thumbnails: arrayOf(shape({ id: string, type: string, url: string, videoStill: string })),
    zoom: arrayOf(shape({ id: string, type: string, url: string, videoStill: string })),
    highResolution: arrayOf(shape({ id: string, type: string, url: string }))
  }),
  product: shape({
    identifiers: shape({ brandName: string, productLabel: string }),
    itemId: string
  }),
  thumbnails: arrayOf(shape({})),
  overrideData: shape({ galleryLabel: string, brandNameLabel: string }),
  overlayOpened: bool.isRequired,
  configuredProductLabel: string,
  videoAnalyticsData: shape({ position: string.isRequired }).isRequired,
  itemId: string,
  showAvataarAR: bool,
  showAvataarDefault: bool,
  initialType: string,
  initialId: string,
  selectedImageIndex: number
};

export const mediaGalleryHorizontalPropTypes = {
  collectionId: string,
  mainImageFetchPriority: oneOf([
    'high',
    'low',
    'auto'
  ]),
  hideProductImagesLabel: bool,
  highlight: bool,
  highRes: bool,
  hover: bool,
  itemId: string,
  configId: string,
  marqueeRef: oneOfType([
    func,
    shape({})
  ]),
  overrideData: shape({
    media: shape({}),
    galleryLabel: string,
    brandNameLabel: string
  }),
  showLoader: bool
};

export const overlayCarouselDefaultProps = {
  onSlideChange: undefined,
  SwiperProps: {},
  zoomImages: [],
  highResolution: [],
  videoAnalyticsData: {},
  itemId: '',
  showAvataarAR: false,
  showAvataarDefault: false
};

export const overlayCarouselPropTypes = {
  images: arrayOf(shape({
    id: string,
    url: string,
    type: string
  })).isRequired,
  selectedIndex: number.isRequired,
  onSlideChange: func,
  SwiperProps: shape({
    onSwiper: func,
    onSlideChange: func,
    onSlideChangeTransitionEnd: func
  }),
  zoomImages: arrayOf(shape({ id: string, url: string })),
  highResolution: arrayOf(shape({ id: string, url: string })),
  videoAnalyticsData: shape({ position: string, section: string }),
  itemId: string,
  showAvataarAR: bool,
  showAvataarDefault: bool
};

export const overlayHeaderDefaultProps = {
  brand: ''
};

export const overlayHeaderPropTypes = {
  label: string.isRequired,
  brand: string
};

export const overlayTilesDefaultProps = {
  onTileClick: () => {},
  selectedType: ''
};

export const overlayTilesPropTypes = {
  media: shape({
    thumbnails: arrayOf(shape({
      id: string,
      type: string,
      url: string
    })).isRequired
  }).isRequired,
  onTileClick: func,
  selectedType: string,
  groups: objectOf(arrayOf(shape({
    id: string,
    type: string,
    url: string
  }))).isRequired
};

export const paintManualImageDefaultProps = {
  asset: null,
  alt: ''
};

export const paintManualImagePropTypes = {
  asset: shape({
    brandLogo: string
  }),
  alt: string
};

export const paintRBGImageDefaultProps = {
  asset: null,
  alt: ''
};

export const paintRBGImagePropTypes = {
  asset: shape({
    rgb: shape({
      red: number,
      green: number,
      blue: number
    }),
    brandLogo: string
  }),
  alt: string
};

export const thumbnailDefaultProps = {
  thumbnail: {},
  thumbnailIndex: 0,
  totalThumbnailsToShow: 0,
  thumbnailCount: 0,
  availableThumbnails: [],
  mainImageList: [],
  onThumbnailEvent: () => {},
  thumbnailSize: 'md',
  grid: false
};

export const thumbnailOverlayDefaultProps = {
  iconProps: {},
  className: '',
  style: {}
};

export const thumbnailOverlayPropTypes = {
  icon: elementType.isRequired,
  iconProps: shape({}),
  className: string,
  style: shape({})
};

export const thumbnailPropTypes = {
  thumbnail: shape({
    isSelected: bool,
    id: string,
    url: string,
    type: string
  }),
  thumbnailIndex: number,
  totalThumbnailsToShow: number,
  thumbnailCount: number,
  availableThumbnails: arrayOf(shape({
    id: string,
    url: string,
    type: string
  })),
  mainImageList: arrayOf(shape({
    id: string,
    url: string,
    type: string
  })),
  onThumbnailEvent: func,
  thumbnailSize: oneOf(['sm', 'md', 'lg']),
  grid: bool
};

export const thumbnailsDefaultProps = {
  thumbnailList: [],
  mainImageList: [],
  totalThumbnailsToShow: undefined,
  originalThumbnailCount: undefined,
  grid: false
};

export const thumbnailsPropTypes = {
  thumbnailList: arrayOf(shape({
    id: string,
    url: string,
    type: string
  })),
  mainImageList: arrayOf(shape({
    id: string,
    url: string,
    type: string
  })),
  selectedThumbnailIndex: number.isRequired,
  totalThumbnailsToShow: number,
  originalThumbnailCount: number,
  onThumbnailEvent: func.isRequired,
  grid: bool
};